import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../redux/store";
import baseUrl from "../../api/baseUrl";
import BottomTab from "../../components/pagination/BottomTab";
import DeleteDialog from "../../components/modal/delete/DeleteDialog";
import {
  setPatients,
  setPatientsCount,
} from "../../redux/patients/patientsAction";
import { Circles } from "react-loader-spinner";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import PatientViewModal from "./components/PatientViewModal";
import ExistingTreatmentConfirmModal, {
  sortTreatmentsByUpdatedDesc,
} from "./components/ExistingTreatmentConfirmModal";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PhoneIcon from "@mui/icons-material/Phone";
import CallIcon from "@mui/icons-material/Call";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import FilterListIcon from "@mui/icons-material/FilterList";
import BadgeIcon from "@mui/icons-material/Badge";
import {
  PATIENT_TYPE_STYLES,
  formatPatientTypeLabel,
  patientTypeLabelFromItem,
} from "../appointment/viewappointment/components/appointmentViewShared";

type SearchByOption = "all" | "fullName" | "regNo" | "dob";

const SEARCH_FILTER_OPTIONS: { value: SearchByOption; label: string }[] = [
  { value: "all", label: "All" },
  { value: "fullName", label: "Full Name" },
  { value: "regNo", label: "Reg No" },
  { value: "dob", label: "DOB" },
];

interface Patient {
  regNo: string;
  fullName: string;
  title?: string;
  address: string;
  nic: string;
  mobileNo: string;
  email?: string;
  dob: string;
  hasid: boolean;
  _id: string;
  patientTypeLabel?: string;
  registerdDate?: string;
  createdAt?: string;
}

const getFullNameWithTitle = (patient?: Patient) => {
  const title = patient?.title?.trim();
  return `${title ? `${title}.` : ""} ${patient?.fullName || ""}`.trim();
};

const getWhatsAppLink = (mobileNo?: string) => {
  const digitsOnly = mobileNo?.replace(/\D/g, "");
  return digitsOnly ? `https://wa.me/${digitsOnly}` : null;
};

function PatientVisitTypeBadge({ patient }: { patient: Patient }) {
  const label = patientTypeLabelFromItem(patient);
  return (
    <span
      className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold border ${
        PATIENT_TYPE_STYLES[label] || PATIENT_TYPE_STYLES["Existing Patient"]
      }`}
    >
      {formatPatientTypeLabel(label)}
    </span>
  );
}

// Mobile Card Component
const PatientCard: React.FC<{
  patient: Patient;
  onView: (id: string) => void;
  onEdit: (id: string, e: React.MouseEvent) => void;
  onAddTreatment: (id: string, e: React.MouseEvent) => void;
  onDelete: (id: string, e: React.MouseEvent) => void;
  canDelete?: Boolean | false;
}> = React.memo(
  ({ patient, onView, onEdit, onAddTreatment, onDelete, canDelete }) => {
    const formatAddress = () => {
      if (typeof patient?.address === "object" && patient?.address !== null) {
        const addr = patient.address as any;
        return `${addr.street || ""} ${addr.no || ""}, ${addr.city || ""}, ${
          addr.district || ""
        }`.trim();
      }
      return patient?.address || "";
    };

    return (
      <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-md transition-colors dark:border-slate-700 dark:bg-slate-800">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-md bg-gradient-to-br from-indigo-400 to-indigo-500 dark:from-indigo-500 dark:to-indigo-600">
              {patient?.fullName?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-1.5">
                <p className="font-semibold text-slate-800 dark:text-slate-200">
                  {getFullNameWithTitle(patient)}
                </p>
                <PatientVisitTypeBadge patient={patient} />
              </div>
              <span className="px-2 py-0.5 text-xs font-medium rounded-full border transition-colors bg-indigo-50 text-indigo-600 border-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700">
                {patient?.regNo}
              </span>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-2 text-sm mb-4">
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <CalendarTodayIcon sx={{ fontSize: 16 }} className="text-slate-400" />
            <span>{patient?.dob?.split("T")[0]}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
            <PhoneIcon sx={{ fontSize: 16 }} className="text-blue-500" />
            {getWhatsAppLink(patient?.mobileNo) && (
              <a
                href={getWhatsAppLink(patient?.mobileNo)!}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-1.5 rounded-lg transition-colors bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50"
                title="Message on WhatsApp"
              >
                <WhatsAppIcon sx={{ fontSize: 16 }} />
              </a>
            )}
            {patient?.mobileNo && (
              <a
                href={`tel:${patient?.mobileNo}`}
                onClick={(e) => e.stopPropagation()}
                className="p-1.5 rounded-lg transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                title="Make Call"
              >
                <CallIcon sx={{ fontSize: 16 }} />
              </a>
            )}
            <span className="flex-1">{patient?.mobileNo}</span>
          </div>
          <div className="flex items-start gap-2 text-slate-600 dark:text-slate-300">
            <LocationOnIcon sx={{ fontSize: 16 }} className="text-slate-400" />
            <span className="line-clamp-2">{formatAddress()}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-3 border-t border-slate-100 dark:border-slate-700 transition-colors">
          <button
            onClick={() => onView(patient?._id)}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
          >
            <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
            View
          </button>
          <button
            onClick={(e) => onEdit(patient?._id, e)}
            className="p-2 rounded-lg transition-colors bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50"
          >
            <BorderColorIcon sx={{ fontSize: 18 }} />
          </button>
          <button
            onClick={(e) => onAddTreatment(patient?._id, e)}
            className="p-2 rounded-lg transition-colors bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
          >
            <MedicalInformationIcon sx={{ fontSize: 18 }} />
          </button>
          {patient?.hasid && canDelete && (
            <button
              onClick={(e) => onDelete(patient?._id, e)}
              className="p-2 rounded-lg transition-colors bg-red-50 text-red-500 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </button>
          )}
        </div>
      </div>
    );
  }
);

// Desktop Table Row Component
const PatientRow: React.FC<{
  patient: Patient;
  onView: (id: string) => void;
  onEdit: (id: string, e: React.MouseEvent) => void;
  onAddTreatment: (id: string, e: React.MouseEvent) => void;
  onDelete: (id: string, e: React.MouseEvent) => void;
  canDelete?: Boolean | false;
}> = React.memo(
  ({ patient, onView, onEdit, onAddTreatment, onDelete, canDelete }) => {
    const formatAddress = () => {
      if (typeof patient?.address === "object" && patient?.address !== null) {
        const addr = patient.address as any;
        return `${addr.street || ""} ${addr.no || ""}, ${addr.city || ""}, ${
          addr.district || ""
        }, ${addr.province || ""}, ${addr.country || ""}`.trim();
      }
      return patient?.address || "";
    };

    return (
      <tr className="border-b border-slate-100 transition-colors hover:bg-slate-50/50 dark:border-slate-700 dark:hover:bg-slate-700/50">
        <td className="px-3 py-2">
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full border transition-colors bg-indigo-50 text-indigo-600 border-indigo-100 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-700">
            {patient?.regNo}
          </span>
        </td>
        <td className="px-3 py-2">
          <span className="font-medium text-sm text-slate-800 dark:text-slate-200">
            {getFullNameWithTitle(patient)}
          </span>
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            {getWhatsAppLink(patient?.mobileNo) && (
              <a
                href={getWhatsAppLink(patient?.mobileNo)!}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-1 rounded-lg transition-colors bg-green-50 text-green-600 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50"
                title="Message on WhatsApp"
              >
                <WhatsAppIcon sx={{ fontSize: 14 }} />
              </a>
            )}
            {patient?.mobileNo && (
              <a
                href={`tel:${patient?.mobileNo}`}
                onClick={(e) => e.stopPropagation()}
                className="p-1 rounded-lg transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
                title="Make Call"
              >
                <CallIcon sx={{ fontSize: 14 }} />
              </a>
            )}
            <span className="text-xs text-slate-600 dark:text-slate-300">
              {patient?.mobileNo}
            </span>
          </div>
        </td>
        <td className="px-3 py-2 text-xs max-w-[180px] truncate text-slate-600 dark:text-slate-300">
          {patient?.email}
        </td>
        <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
          {patient?.dob?.split("T")[0]}
        </td>
        <td className="px-3 py-2 text-xs max-w-[180px] truncate text-slate-600 dark:text-slate-300">
          {formatAddress()}
        </td>
        <td className="px-3 py-2">
          <PatientVisitTypeBadge patient={patient} />
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center justify-end gap-1.5">
            <button
              onClick={() => onView(patient?._id)}
              className="p-1.5 rounded-lg transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50"
              title="View"
            >
              <RemoveRedEyeIcon sx={{ fontSize: 16 }} />
            </button>
            <button
              onClick={(e) => onEdit(patient?._id, e)}
              className="p-1.5 rounded-lg transition-colors bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:hover:bg-amber-900/50"
              title="Edit"
            >
              <BorderColorIcon sx={{ fontSize: 16 }} />
            </button>
            <button
              onClick={(e) => onAddTreatment(patient?._id, e)}
              className="p-1.5 rounded-lg transition-colors bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
              title="Add Treatment"
            >
              <MedicalInformationIcon sx={{ fontSize: 16 }} />
            </button>
            {patient?.hasid && canDelete && (
              <button
                onClick={(e) => onDelete(patient?._id, e)}
                className="p-1.5 rounded-lg transition-colors bg-red-50 text-red-500 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                title="Delete"
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  }
);

const ActivePatient: React.FC = () => {
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingtreatment, setLoadingtreatment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletemodal, setdeletemodal] = useState(false);
  const [canDelete, setcanDelete] = useState(false);
  const [deleteId, setdeleteId] = useState<string | null>(null);
  const [treatmentConfirmModal, setTreatmentConfirmModal] = useState(false);
  const [pendingTreatmentId, setPendingTreatmentId] = useState<string | null>(
    null
  );
  const [previousTreatments, setPreviousTreatments] = useState<any[]>([]);
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const patients = useSelector((state: RootState) => state.patients.patients);
  const patientscount = useSelector(
    (state: RootState) => state.patients.patientsCount
  );
  const [search, setsearch] = useState("");
  const [searchBy, setSearchBy] = useState<SearchByOption>("all");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewPatientData, setViewPatientData] = useState<any>(null);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const activePatientsRequestRef = useRef<AbortController | null>(null);

  const handleClickGoViewPatient = useCallback(
    (id: string) => {
      
      setLoadingPatient(true);
      setViewModalOpen(true);
      baseUrl
        .get("patient/findById", {
          headers: {
            patientid: id,
          },
        })
        .then((response) => {
          setViewPatientData(response.data.data);
          setLoadingPatient(false);
        })
        .catch((error) => {
          console.error(error);
          setLoadingPatient(false);
        });
    },
    []
  );

  const closeViewModal = useCallback(() => {
    setViewModalOpen(false);
    setViewPatientData(null);
  }, []);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as "light" | "dark";
    const htmlTheme = document.documentElement.getAttribute("data-theme") as "light" | "dark";
    
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (htmlTheme) {
      setTheme(htmlTheme);
    }

    // Listen for theme changes
    const observer = new MutationObserver(() => {
      const currentTheme = document.documentElement.getAttribute("data-theme") as "light" | "dark";
      if (currentTheme) {
        setTheme(currentTheme);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  const handleClickGoEditPatient = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(`/client/patients/edit-patients/${id}`);
    },
    [navigate]
  );

  const createTreatmentAndNavigate = useCallback(
    (id: string) => {
      setLoadingtreatment(true);
      baseUrl
        .post("treatment/create-treatment", { patientId: id })
        .then((response) => {
          setLoadingtreatment(false);
          navigate(
            `/client/treatment-diagnose/add-treatment/treatment-table/${response.data.data._id}`
          );
        })
        .catch((error) => {
          console.error(error);
          setLoadingtreatment(false);
          toast.error(
            error?.response?.data?.message || "Could not create treatment"
          );
        });
    },
    [navigate]
  );

  const handleTreatmentClick = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setLoadingtreatment(true);
      baseUrl
        .get("/patient/findTreatmentByPid", { headers: { patientid: id } })
        .then((res) => {
          const treatments = res.data?.data || [];
          if (Array.isArray(treatments) && treatments.length > 0) {
            setLoadingtreatment(false);
            setPendingTreatmentId(id);
            setPreviousTreatments(sortTreatmentsByUpdatedDesc(treatments));
            setTreatmentConfirmModal(true);
            return;
          }
          createTreatmentAndNavigate(id);
        })
        .catch((error) => {
          console.error(error);
          setLoadingtreatment(false);
          toast.error(
            error?.response?.data?.message ||
              "Could not check existing treatments"
          );
        });
    },
    [createTreatmentAndNavigate]
  );

  const confirmCreateTreatment = useCallback(() => {
    if (!pendingTreatmentId) return;
    setTreatmentConfirmModal(false);
    const id = pendingTreatmentId;
    setPendingTreatmentId(null);
    setPreviousTreatments([]);
    createTreatmentAndNavigate(id);
  }, [pendingTreatmentId, createTreatmentAndNavigate]);

  const cancelCreateTreatment = useCallback(() => {
    setTreatmentConfirmModal(false);
    setPendingTreatmentId(null);
    setPreviousTreatments([]);
  }, []);

  const openExistingTreatment = useCallback(
    (treatmentId: string) => {
      if (!treatmentId) return;
      setTreatmentConfirmModal(false);
      setPendingTreatmentId(null);
      setPreviousTreatments([]);
      navigate(
        `/client/treatment-diagnose/add-treatment/treatment-table/${treatmentId}`
      );
    },
    [navigate]
  );

  const handleDiagnosisClick = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setLoadingtreatment(true);
      baseUrl
        .post("treatment/create-treatment", { patientId: id })
        .then((response) => {
          setLoadingtreatment(false);
          navigate(
            `/client/treatment-diagnose/add-treatment/diagnose/${response.data.data._id}`
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    [navigate]
  );

  const confirmDelete = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setdeleteId(id);
      setdeletemodal(true);
    },
    [setdeleteId, setdeletemodal]
  );

  const cancelDelete = useCallback(() => {
    setdeletemodal(false);
    setdeleteId(null);
  }, []);

  const getData = useCallback(
    (searchTerm: string, pageNum: number, filterBy: SearchByOption) => {
      setLoading(true);
      setError(null);
      const controller = new AbortController();
      if (activePatientsRequestRef.current) {
        activePatientsRequestRef.current.abort();
      }
      activePatientsRequestRef.current = controller;
      baseUrl
        .get("client/patient/find-all-patients", {
          headers: {
            status: true,
            page: pageNum,
            keyword: searchTerm,
            searchBy: filterBy,
          },
          signal: controller.signal,
        })
        .then((response) => {
          if (activePatientsRequestRef.current !== controller) return;
          setLoading(false);
          if (response.data.status === 204) {
            dispatch(setPatients([]));
            dispatch(setPatientsCount(0));
          } else {
            dispatch(setPatients(response.data.data));
            dispatch(setPatientsCount(response.data.count));
          }
        })
        .catch((error) => {
          if (activePatientsRequestRef.current !== controller) return;
          const isCanceled =
            error?.code === "ERR_CANCELED" ||
            error?.name === "CanceledError" ||
            error?.message?.toLowerCase?.() === "canceled";
          if (isCanceled) return;
          setError(error.response?.data?.message || "Error fetching data");
          setLoading(false);
        });
    },
    [dispatch]
  );

  const handlePatientSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setsearch(e.target.value);
    setPage(1);
  };

  const handleSearchByChange = (value: SearchByOption) => {
    setSearchBy(value);
    setsearch("");
    setPage(1);
  };

  useEffect(() => {
    const delay = search && searchBy !== "dob" ? 300 : 0;
    const timer = window.setTimeout(() => {
      getData(search, page, searchBy);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [getData, search, searchBy, page]);

  const showFullLoader = loading && !patients?.length;
  const isRefreshing = loading && !!patients?.length;

  const searchPlaceholder =
    searchBy === "fullName"
      ? "Search by full name..."
      : searchBy === "regNo"
      ? "Search by reg no..."
      : searchBy === "dob"
      ? "Select date of birth"
      : "Search patients...";

  const handleDeleteClick = useCallback(() => {
    if (!deleteId) return;
    baseUrl
      .delete("patient/deleteLastPatientById", {
        headers: { patientid: deleteId },
      })
      .then((response) => {
        if (response.data?.success) {
          setdeletemodal(false);
          setdeleteId(null);
          toast.success("Patient deleted successfully");
          getData(search, page, searchBy);
        }
      })
      .catch((error) => {
        console.error(error);
        toast.error(
          error?.response?.data?.message ||
            "Could not delete this patient. They may be linked to other records."
        );
      });
  }, [deleteId, page, getData, search, searchBy]);

  const onPageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const toggleCanDelete = useCallback(() => {
    setcanDelete((prev) => !prev);
  }, []);

  // Mobile Cards
  const mobileCards = useMemo(() => {
    return patients?.map((patient: any) => (
      <PatientCard
        key={patient._id}
        patient={patient}
        onView={handleClickGoViewPatient}
        onEdit={handleClickGoEditPatient}
        onAddTreatment={handleTreatmentClick}
        onDelete={confirmDelete}
        canDelete={canDelete}
      />
    ));
  }, [
    patients,
    handleClickGoViewPatient,
    handleClickGoEditPatient,
    handleTreatmentClick,
    confirmDelete,
    canDelete,
  ]);

  // Desktop Table Rows
  const renderedTableRows = useMemo(() => {
    return patients?.map((patient: any) => (
      <PatientRow
        key={patient._id}
        patient={patient}
        onView={handleClickGoViewPatient}
        onEdit={handleClickGoEditPatient}
        onAddTreatment={handleTreatmentClick}
        onDelete={confirmDelete}
        canDelete={canDelete}
      />
    ));
  }, [
    patients,
    handleClickGoViewPatient,
    handleClickGoEditPatient,
    handleTreatmentClick,
    confirmDelete,
    canDelete,
  ]);

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className="rounded-[8px] shadow-lg border p-3 transition-colors duration-300 bg-white border-slate-100 shadow-slate-200/50 dark:bg-slate-800 dark:border-slate-700 dark:shadow-slate-900/50">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2.5">
          {/* Search & Filter */}
          <div className="flex flex-col gap-2 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <FilterListIcon
                className="text-slate-400 dark:text-slate-500"
                sx={{ fontSize: 16 }}
              />
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mr-0.5">
                Search by:
              </span>
              {SEARCH_FILTER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSearchByChange(option.value)}
                  className={`h-8 px-3 rounded-lg text-xs font-semibold transition-all duration-200 ${
                    searchBy === option.value
                      ? "bg-gradient-to-r from-indigo-500 to-indigo-600 text-white shadow-sm dark:from-indigo-600 dark:to-indigo-700"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:hover:bg-slate-600"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className="relative flex-1 max-w-md">
              {searchBy === "dob" ? (
                <CalendarTodayIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-slate-400"
                  sx={{ fontSize: 16 }}
                />
              ) : searchBy === "regNo" ? (
                <BadgeIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-slate-400"
                  sx={{ fontSize: 16 }}
                />
              ) : (
                <SearchIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-10 text-slate-400"
                  sx={{ fontSize: 18 }}
                />
              )}
              <input
                type={searchBy === "dob" ? "date" : "search"}
                placeholder={searchPlaceholder}
                onChange={handlePatientSearch}
                value={search}
                className="w-full h-9 pl-10 pr-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all text-xs font-medium placeholder:text-slate-400 bg-slate-50 border-slate-200 text-slate-700 focus:bg-white dark:bg-slate-700 dark:border-slate-600 dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:bg-slate-600"
              />
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 flex-wrap">
            {!canDelete ? (
              <button
                type="button"
                onClick={toggleCanDelete}
                className="h-9 px-3.5 rounded-lg font-semibold transition-all duration-300 text-xs shadow-sm hover:shadow-md active:scale-95 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white dark:from-red-600 dark:to-red-700 dark:hover:from-red-700 dark:hover:to-red-800"
              >
                Enable Delete
              </button>
            ) : (
              <button
                type="button"
                onClick={toggleCanDelete}
                className="h-9 px-3.5 rounded-lg font-semibold transition-all duration-300 text-xs shadow-sm hover:shadow-md active:scale-95 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white dark:from-emerald-600 dark:to-emerald-700 dark:hover:from-emerald-700 dark:hover:to-emerald-800"
              >
                Disable Delete
              </button>
            )}
            <div className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg border transition-colors bg-slate-100 border-slate-200 dark:bg-slate-700 dark:border-slate-600">
              <PeopleAltIcon className="text-indigo-500 dark:text-indigo-400" sx={{ fontSize: 18 }} />
              <span className="font-bold text-xs text-slate-800 dark:text-slate-200">{patientscount}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400">Patients</span>
            </div>
            <button
              onClick={() => navigate("/client/patients/quick-add")}
              className="h-9 px-3.5 rounded-lg font-semibold transition-all duration-300 flex items-center gap-1.5 shadow-sm hover:shadow-md active:scale-95 text-xs bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white dark:from-indigo-600 dark:to-indigo-700 dark:hover:from-indigo-700 dark:hover:to-indigo-800"
            >
              <PersonAddIcon sx={{ fontSize: 18 }} />
              <span className="hidden sm:inline">Add Patient</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className={`md:hidden space-y-3 transition-opacity duration-200 ${isRefreshing ? "opacity-60 pointer-events-none" : ""}`}>
        {showFullLoader ? (
          <div className="rounded-2xl p-8 text-center transition-colors bg-white dark:bg-slate-800">
            <Circles height="40" width="40" color="#6366f1" ariaLabel="loading" />
          </div>
        ) : error ? (
          <div className="rounded-2xl p-8 text-center text-red-500 transition-colors bg-white dark:bg-slate-800">
            {error}
          </div>
        ) : patients?.length === 0 ? (
          <div className="rounded-2xl py-12 text-center shadow-md transition-colors bg-white dark:bg-slate-800 dark:border dark:border-slate-700">
            <PeopleAltIcon className="text-slate-300 dark:text-slate-500" sx={{ fontSize: 48 }} />
            <p className="text-slate-500 font-medium dark:text-slate-400">No patients found</p>
          </div>
        ) : (
          mobileCards
        )}
      </div>

      {/* Desktop Table View */}
      <div className={`hidden md:block rounded-[8px] shadow-lg border overflow-hidden transition-colors duration-200 bg-white border-slate-100 shadow-slate-200/50 dark:bg-slate-800 dark:border-slate-700 dark:shadow-slate-900/50 ${isRefreshing ? "opacity-60 pointer-events-none" : ""}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50/80 dark:bg-slate-700/80">
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  Reg No
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  Name
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  Phone Number
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  Email
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  DOB
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  Address
                </th>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  Type
                </th>
                <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-300">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {showFullLoader ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <Circles height="40" width="40" color="#6366f1" ariaLabel="loading" />
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-red-500">
                    {error}
                  </td>
                </tr>
              ) : patients?.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <PeopleAltIcon
                      className="text-slate-300 dark:text-slate-500"
                      sx={{ fontSize: 48 }}
                    />
                    <p className="text-slate-500 font-medium dark:text-slate-400">No patients found</p>
                  </td>
                </tr>
              ) : (
                renderedTableRows
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <BottomTab title="Patient" count={patientscount} onPageChange={onPageChange} />

      {/* Delete Modal */}
      {deletemodal && (
        <DeleteDialog
          onDelete={handleDeleteClick}
          onCancel={cancelDelete}
          title={"Are you sure you want to delete this patient's record?"}
          deletes="DELETE"
        />
      )}

      {/* Confirm create treatment when patient already has treatments */}
      <ExistingTreatmentConfirmModal
        open={treatmentConfirmModal}
        treatments={previousTreatments}
        onCancel={cancelCreateTreatment}
        onCreateNew={confirmCreateTreatment}
        onOpenTreatment={openExistingTreatment}
      />

      {/* Loading Overlay */}
      {loadingtreatment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Circles height="60" width="60" color="#6366f1" ariaLabel="loading" />
        </div>
      )}

      {/* Patient View Modal */}
      <PatientViewModal
        isOpen={viewModalOpen}
        onClose={closeViewModal}
        patientData={viewPatientData}
        loading={loadingPatient}
        theme={theme}
        onEdit={handleClickGoEditPatient}
        onAddTreatment={handleTreatmentClick}
        onAddDiagnosis={handleDiagnosisClick}
        onDelete={confirmDelete}
        canDelete={canDelete}
      />
    </div>
  );
};

export default ActivePatient;
